import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { fmtDate, fmtMoney } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_OPTIONS = ["all", "draft", "published", "rejected", "unpublished"];

export default function Experiences() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    const params = { page, page_size: 25 };
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    api.get("/experiences", { params })
      .then(({ data }) => { if (active) setData(data); })
      .catch((error) => { if (active) { setData({ items: [], total: 0 }); setError(true); toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Unable to load experiences."); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [search, status, page]);

  const columns = [
    { key: "title", header: "Experience", render: (r) => (
      <div>
        <div className="font-medium max-w-[280px] truncate" data-testid={`exp-title-${r.id}`}>{r.title}</div>
        <div className="text-xs text-muted-foreground">{r.supplier_name} · {r.destination}</div>
      </div>
    )},
    { key: "category", header: "Category", render: (r) => <span className="text-xs">{r.category}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "price_from", header: "Price from", render: (r) => <span className="tabular-nums">{fmtMoney(r.price_from, r.currency)}</span> },
    { key: "duration_hours", header: "Duration", render: (r) => <span className="text-xs">{r.duration_hours == null ? "—" : `${r.duration_hours}h`}</span> },
    { key: "rating", header: "Rating", render: (r) => <span className="tabular-nums">{r.rating > 0 ? r.rating.toFixed(2) : "—"}</span> },
    { key: "bookings_count", header: "Bookings", render: (r) => <span className="tabular-nums">{r.bookings_count}</span> },
    { key: "last_updated", header: "Updated", render: (r) => <span className="text-xs text-muted-foreground">{fmtDate(r.last_updated)}</span> },
  ];

  return (
    <div data-testid="experiences-page">
      <PageHeader title="Experiences" description="Browse supplier experiences. Use search and status filters to find a listing." />
      <div className="pt-6">
        <DataTable
          columns={columns}
          rows={data.items}
          total={data.total}
          page={page}
          pageSize={25}
          loading={loading}
          onPageChange={setPage}
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder="Search title, destination, supplier…"
          toolbar={
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="experiences-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          }
          onRowClick={(r) => nav(`/experiences/${r.id}`)}
          emptyTitle={error ? "Unable to load experiences" : "No experiences"}
          testIdPrefix="experiences"
        />
      </div>
    </div>
  );
}
